import React, { useState, useEffect } from 'react';
import { apiUrl } from '../config/api';
import './HistoricalComparison.css';

interface Scan {
  scanId: string;
  timestamp: string;
  profile: string;
  summary: {
    pass: boolean;
    failCount: number;
  };
  fileName?: string;
  screenshot?: string;
}

export const HistoricalComparison: React.FC = () => {
  const [scans, setScans] = useState<Scan[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScans();
  }, []);

  const loadScans = async () => {
    try {
      const response = await fetch(apiUrl('/scans?limit=5'), { credentials: 'include' });
      const data = await response.json();

      const scansWithDetails = await Promise.all(
        (data.scans || []).map(async (scan: any) => {
          try {
            const detailResponse = await fetch(apiUrl(`/scans/${scan.scanId}`), { credentials: 'include' });
            const detailData = await detailResponse.json();
            return {
              ...scan,
              fileName: detailData.result?.metadata?.originalFileName || 'Unknown',
              screenshot: detailData.result?.runtime?.screenshot?.base64
            };
          } catch {
            return { ...scan, fileName: 'Unknown' };
          }
        })
      );
      
      setScans(scansWithDetails);
    } catch (error) {
      console.error('Failed to load scans:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="historical-comparison loading">Loading history...</div>;
  }

  if (scans.length === 0) {
    return (
      <div className="historical-comparison">
        <div className="comparison-header">
          <h3>Recent Scans</h3>
        </div>
        <div className="empty-state">
          <p>No previous scans found. Complete more validations to see history.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="historical-comparison">
      <div className="comparison-header">
        <h3>Recent Scans</h3>
      </div>
      <div className="scans-table">
        <table>
          <thead>
            <tr>
              <th>Preview</th>
              <th>Status</th>
              <th>File Name</th>
              <th>Scan ID</th>
              <th>Date</th>
              <th>Profile</th>
            </tr>
          </thead>
          <tbody>
            {scans.map((scan) => (
              <tr key={scan.scanId}>
                <td>
                  {scan.screenshot ? (
                    <img 
                      src={`data:image/jpeg;base64,${scan.screenshot}`}
                      alt="Preview"
                      style={{
                        width: '50px',
                        height: '38px',
                        objectFit: 'cover',
                        border: '1px solid #e5e7eb',
                        borderRadius: '3px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '50px',
                      height: '38px',
                      backgroundColor: '#f9fafb',
                      border: '1px dashed #d1d5db',
                      borderRadius: '3px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      color: '#9ca3af',
                      textAlign: 'center',
                      padding: '2px'
                    }}>
                      No preview
                    </div>
                  )}
                </td>
                <td>
                  <div className={`status-pill ${scan.summary?.pass ? 'pass' : 'fail'}`}>
                    {scan.summary?.pass ? (
                      <><span className="status-icon">✓</span> Pass</>
                    ) : (
                      <><span className="status-icon">✗</span> {scan.summary?.failCount || 0} Failed</>
                    )}
                  </div>
                </td>
                <td className="filename-cell">{scan.fileName}</td>
                <td>
                  <a 
                    href={`http://localhost:5173/scans/${scan.scanId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="scan-id-link"
                    title="View full report"
                  >
                    {scan.scanId}
                  </a>
                </td>
                <td className="date-cell">
                  {new Date(scan.timestamp).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </td>
                <td>
                  <span className="profile-tag">{scan.profile}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};